import { Badge, Button, Message, Popover, Tabs, Typography } from "@arco-design/web-react";
import { Bell } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useMarkNotificationMessageReadMutation,
  useNotificationMessagesQuery,
  useNotificationMessageSummaryQuery,
} from "../runtime";
import { resolveSafeNotificationTarget } from "../service";
import type { NotificationMessage, NotificationMessageView } from "../types";
import { NotificationDetailDrawer } from "./NotificationDetailDrawer";
import { NotificationMessageList } from "./NotificationMessageList";
import "./NotificationCenter.css";

/** 后台顶栏的消息 Badge、最近消息弹层和普通通知详情入口。 */
export function NotificationCenterTrigger() {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<Exclude<NotificationMessageView, "all">>("action");
  const [detail, setDetail] = useState<NotificationMessage | null>(null);
  const summaryQuery = useNotificationMessageSummaryQuery();
  const messageQuery = useNotificationMessagesQuery({ view: activeView, page: 1, pageSize: 5 });
  const markRead = useMarkNotificationMessageReadMutation();

  const markReadWithoutBlocking = async (messageId: string) => {
    try {
      await markRead.mutateAsync(messageId);
    } catch {
      // 已读写入暂时失败不能阻断真实站内业务入口。
    }
  };

  const openNotice = async (message: NotificationMessage) => {
    setDetail(message);
    await markReadWithoutBlocking(message.id);
  };

  const openAction = async (message: NotificationMessage) => {
    const target = resolveSafeNotificationTarget(message.targetUrl);
    if (!target) {
      Message.error("消息跳转地址无效，请联系消息来源功能负责人。");
      return;
    }
    navigate(target);
    void markReadWithoutBlocking(message.id);
  };

  const popoverContent = (
    <div className="notification-center-popover" aria-label="最近消息">
      <div className="notification-center-popover__header">
        <Typography.Title heading={6} style={{ margin: 0 }}>通知公告</Typography.Title>
        <Button type="text" size="mini" onClick={() => navigate("/notifications/inbox")}>查看全部</Button>
      </div>
      <Tabs
        activeTab={activeView}
        onChange={(key) => setActiveView(key as Exclude<NotificationMessageView, "all">)}
      >
        <Tabs.TabPane key="action" title={`待处理 ${summaryQuery.data?.openActionCount ?? 0}`} />
        <Tabs.TabPane key="notice" title={`通知 ${summaryQuery.data?.unreadNoticeCount ?? 0}`} />
      </Tabs>
      <div className="notification-center-popover__list">
        <NotificationMessageList
          compact
          items={messageQuery.data?.items ?? []}
          loading={messageQuery.isLoading}
          error={messageQuery.isError}
          onRetry={() => void messageQuery.refetch()}
          onOpenNotice={(message) => void openNotice(message)}
          onOpenAction={(message) => void openAction(message)}
        />
      </div>
    </div>
  );

  return (
    <>
      <Popover
        trigger="click"
        position="br"
        content={popoverContent}
        style={{ width: "min(380px, calc(100vw - 24px))", maxWidth: "none" }}
      >
        <Badge count={summaryQuery.data?.unreadCount ?? 0} maxCount={99} dotStyle={{ boxShadow: "0 0 0 2px #fff" }}>
          <Button
            type="text"
            size="small"
            className="notification-center-trigger__button"
            icon={<Bell size={18} />}
            aria-label={`打开通知公告${summaryQuery.data?.unreadCount ? `，${summaryQuery.data.unreadCount} 条未读` : ""}`}
          />
        </Badge>
      </Popover>
      <NotificationDetailDrawer message={detail} onClose={() => setDetail(null)} />
    </>
  );
}
